import { AppData } from "../../types/app";

const app: AppData = {
  slug: "limenote",
  name: "LimeNote",
  summary: "회의 녹음을 받아써 고칠 수 있는 AI 회의록으로 만드는 웹 서비스",
  category: "업무 도구",
  platforms: ["web"],
  status: "live",
  released_at: "2026-08-22",
  pinned: false,
  icon: "/apps/limenote/icon.svg",
  links: { web: "https://limenote.org" },
  has_account: true,
  operator: { name: "Hellovelop Inc.", email: "limelink2024@gmail.com" },
  role: "Hellovelop 취미 팀의 백엔드 리드. 서비스 구조 설계와 기술 결정, 녹음에서 회의록까지 이어지는 처리 흐름과 결제를 맡았다.",
  why:
    "회의에 집중하면서도 회의가 끝난 뒤 정확한 기록이 남아야 하는 사람을 위해 만들었다. 녹음을 다시 듣고 흩어진 메모로 회의록을 재구성하는 시간을 줄이는 것이 목표다. 내가 필요해서 만들었고 따로 마케팅하지 않았으며, 지금은 회사 동료들이 쓰고 있다.",
  features: [
    { title: "녹음에서 회의록까지", body: "브라우저에서 녹음하거나 파일을 올리면 받아쓰기, 템플릿에 맞춘 AI 정리, 제목과 태그 생성까지 이어진다. 진행 상황은 화면에 실시간으로 보인다." },
    { title: "템플릿 8종과 재변환", body: "회의, 1:1, 결정, 할 일, 회고 등 템플릿 8종이 있다. 녹음 하나를 여러 템플릿으로 다시 변환할 수 있다." },
    { title: "편집과 내보내기", body: "원문 전사와 회의록을 나란히 보며 고친다. Markdown, TXT, Word, PDF로 내려받고 원본 오디오는 mp3로 받는다." },
    { title: "용어집", body: "사람 이름과 사내 용어를 등록하면 회의록에 자동으로 바로잡아 넣는다. 지난 회의에서 후보를 찾아 미리 본 뒤 적용할 수 있다." },
    { title: "외부 공유 링크", body: "로그인 없이 읽을 수 있는 읽기 전용 문서 링크를 만든다. 공유는 언제든 끌 수 있다." },
  ],
  policy: {
    privacy: {
      effective_at: "2026-11-01",
      previous: [
        { effective_at: "2026-09-10", note: "limenote.org/privacy에 게시한 영문 개인정보처리방침" },
      ],
      sections: [
        {
          id: "purpose",
          title: "개인정보의 처리 목적",
          body: [
            "LimeNote(limenote.org, 이하 서비스)는 Hellovelop Inc.가 운영하며, 다음 목적으로만 개인정보를 처리합니다.",
            "회원 식별과 로그인 유지, 녹음 저장과 받아쓰기, AI 회의록 생성과 편집, 공유 링크 제공, 구독과 크레딧 관리, Paddle 결제와 환불 처리, 로그인 확인과 영수증 같은 서비스 안내 메일 발송, 문의 응대, 보안과 부정 이용 방지, 오류 분석, 법령상 의무 이행.",
            "이용자가 분석 쿠키에 동의한 경우에 한해 이용 통계와 화면 개선을 위해 이용 기록을 분석합니다.",
            "개인정보를 판매하지 않고, 회의 콘텐츠를 광고에 쓰지 않으며, 광고 쿠키를 사용하지 않습니다.",
          ],
        },
        {
          id: "items",
          title: "처리하는 개인정보 항목",
          body: [
            "회의 오디오에는 다른 참석자의 목소리와 개인정보가 담길 수 있습니다. 녹음과 업로드에 필요한 동의를 받는 것은 이용자의 책임입니다.",
            "결제 카드 등 결제 수단 정보는 Paddle이 직접 받으며 서비스는 카드 번호를 받거나 저장하지 않습니다.",
            "실시간 코치의 목소리 정보는 생체인식정보에 해당할 수 있어 별도 동의를 받아 처리합니다. 서비스는 브라우저에서 계산한 음성 특징 벡터만 저장하고 목소리 녹음 원본은 저장하지 않습니다.",
            "서비스는 만 14세 미만 아동을 대상으로 하지 않으며, 만 14세 미만 아동의 개인정보를 알면서 수집하지 않습니다.",
            "서비스는 로그인 유지를 위해 자바스크립트가 읽을 수 없는 쿠키를 쓰고, 동의한 경우에만 분석 쿠키를 씁니다. 쿠키 저장은 브라우저 설정에서 거부하거나 삭제할 수 있으며, 로그인 유지 쿠키를 거부하면 로그인할 수 없습니다. 분석 쿠키는 설정의 분석 쿠키 항목에서 언제든 거부하거나 철회할 수 있습니다.",
          ],
          table: {
            head: ["구분", "항목", "수집 시점"],
            rows: [
              ["회원 정보", "이메일, 표시 이름, Google 계정 식별자", "Google 로그인 시"],
              ["로그인 유지", "로그인 갱신 토큰(서버에는 해시로 저장)", "로그인과 갱신 시"],
              ["설정", "화면 언어", "설정 변경 시"],
              ["회의 콘텐츠", "녹음 또는 업로드한 오디오 원본과 mp3 변환본, 전사문, 회의록, 제목, 태그, 회의 날짜", "업로드와 변환 시"],
              ["용어집(선택)", "등록한 사람 이름과 용어", "등록 시"],
              ["공유 링크", "공유 토큰", "변환 완료 시 자동 생성"],
              ["결제 정보", "구매한 플랜, 결제 상태와 식별자, 금액과 통화, 과세용 청구 국가, 크레딧 지급과 사용 내역", "결제와 서비스 이용 시"],
              ["실시간 코치(선택)", "목소리 특징 벡터, 발화 원문과 번역, 근거 자료에서 추출한 텍스트", "코치 이용 시"],
              ["자동 수집", "IP 주소, 브라우저와 기기 정보, 요청 경로, 접속 시각, 오류 기록", "서비스 이용 시"],
              ["분석(선택)", "쿠키와 온라인 식별자, 방문 페이지, 방문 시각, 클릭, 스크롤, 마우스 이동", "분석 쿠키 동의 후"],
              ["브라우저 저장소", "분석 쿠키 동의 상태, 화면 언어", "선택 시"],
            ],
          },
        },
        {
          id: "retention",
          title: "개인정보의 보유와 이용 기간",
          body: [
            "회원 정보, 회의 콘텐츠, 용어집, 실시간 코치 정보는 이용자가 삭제하거나 회원 탈퇴할 때까지 보유합니다. 자동으로 지우는 기간은 따로 두지 않습니다.",
            "로그인 갱신 토큰은 발급 후 30일이 지나면 효력을 잃습니다.",
            "결제와 구독 기록, 청약철회 기록은 「전자상거래 등에서의 소비자보호에 관한 법률」에 따라 5년간 보관합니다.",
            "보안과 오류 분석을 위한 서버 접속 기록은 「통신비밀보호법」에 따라 3개월간 보관한 뒤 삭제합니다.",
            "Google Analytics 4 분석 데이터는 수집일로부터 최대 14개월, Microsoft Clarity 데이터는 30일간 보관됩니다.",
          ],
        },
        {
          id: "third-party",
          title: "개인정보의 제3자 제공",
          body: [
            "서비스는 이용자의 개인정보를 제3자에게 제공하지 않습니다. 법령에 근거가 있거나 수사기관이 법령에 정한 절차에 따라 요구하는 경우는 예외입니다.",
            "이용자가 회의록 공유 링크를 켜면 링크를 가진 누구나 로그인 없이 해당 회의록을 읽을 수 있습니다. 이는 이용자의 선택에 따른 공개이며, 공유는 언제든 끌 수 있습니다.",
          ],
        },
        {
          id: "processors",
          title: "개인정보 처리의 위탁과 국외 이전",
          body: [
            "서비스 제공을 위해 아래 업체에 개인정보 처리를 위탁하며, 업체의 서버가 국외에 있어 개인정보가 국외로 이전됩니다.",
            "이전 시기와 방법: 서비스를 이용할 때 암호화된 네트워크(HTTPS)로 전송합니다. 분석 업체로는 분석 쿠키에 동의한 뒤에만 전송합니다. 실시간 코치의 오디오는 서버를 거치지 않고 브라우저에서 Soniox로 직접 전송됩니다.",
            "거부 방법: 받아쓰기, 회의록 생성, 저장, 결제 업체로의 이전을 거부하면 서비스의 핵심 기능을 이용할 수 없습니다. 분석 쿠키는 설정의 분석 쿠키 항목에서 거부하거나 철회할 수 있으며, 거부해도 서비스 기능은 그대로 쓸 수 있습니다.",
            "Paddle은 판매 대행자(Merchant of Record)로서 결제 화면에서 이용자에게 결제 정보를 직접 받고, 자체 방침에 따라 거래 기록을 보관합니다.",
          ],
          table: {
            head: ["업체", "위탁 업무", "이전 국가", "이전 항목", "연락처", "보유 기간"],
            rows: [
              ["Paddle.com Market Ltd, Paddle.com Inc.", "결제, 청구서 발행, 세금 처리, 환불", "영국, 미국", "이름, 이메일, 결제 정보, 구매 이력", "https://www.paddle.com/legal/privacy", "위탁 계약 종료 시까지, 거래 기록은 Paddle 방침에 따름"],
              ["DeepInfra", "음성 받아쓰기(Whisper)", "미국", "회의 오디오", "https://deepinfra.com", "위탁 목적 달성 또는 위탁 계약 종료 시까지"],
              ["Google LLC (Gemini API)", "회의록, 제목, 태그 생성과 용어집 후보 분석", "미국", "전사문, 용어집", "https://policies.google.com/privacy", "위탁 목적 달성 또는 위탁 계약 종료 시까지"],
              ["Anthropic", "백업 회의록 생성, 실시간 코치 분석과 번역, 코치 자료 텍스트 추출", "미국", "전사문, 코치 발화, 코치 자료", "https://www.anthropic.com/legal/privacy", "위탁 목적 달성 또는 위탁 계약 종료 시까지"],
              ["xAI", "호환 AI 모델(기본 비활성)", "미국", "전사문", "https://x.ai", "위탁 목적 달성 또는 위탁 계약 종료 시까지"],
              ["Cloudflare", "오디오 파일 저장(R2), 콘텐츠 전송, DNS, 보안", "미국", "오디오 파일, IP 주소 등 요청 정보", "https://www.cloudflare.com/privacypolicy/", "오디오는 녹음 삭제 또는 회원 탈퇴 시까지, 그 밖의 정보는 위탁 계약 종료 시까지"],
              ["Resend", "서비스 안내 메일 발송", "미국", "이메일 주소, 메일 본문", "https://resend.com", "위탁 목적 달성 또는 위탁 계약 종료 시까지"],
              ["Google LLC (Google 로그인)", "로그인 인증", "미국", "Google 계정 식별자, 이름, 이메일", "https://policies.google.com/privacy", "위탁 목적 달성 또는 위탁 계약 종료 시까지"],
              ["Google LLC (Google Analytics 4)", "이용 통계, 유입 경로 분석(동의 시)", "미국", "쿠키와 온라인 식별자, 기기와 브라우저 정보, 방문과 페이지 이용 기록", "https://policies.google.com/privacy", "수집일로부터 최대 14개월"],
              ["Microsoft Corporation (Microsoft Clarity)", "히트맵, 세션 리플레이, 화면 개선(동의 시)", "미국", "쿠키 식별자, 마스킹한 IP 주소, 기기와 브라우저 정보, 클릭, 스크롤, 마우스 이동", "https://privacy.microsoft.com", "30일"],
              ["Soniox", "실시간 코치 받아쓰기", "미국", "코치 세션 오디오", "https://soniox.com", "위탁 목적 달성 또는 위탁 계약 종료 시까지"],
              ["TypeSafe (Jev)", "실시간 코치 발화 분류", "업체 고지 국가(문의 시 안내)", "코치 대화 텍스트", "https://typesafe.ai", "위탁 목적 달성 또는 위탁 계약 종료 시까지"],
            ],
          },
        },
        {
          id: "destruction",
          title: "개인정보의 파기 절차와 방법",
          body: [
            "보유 기간이 끝나거나 처리 목적을 달성한 개인정보는 지체 없이 파기합니다. 법령에 따라 보관해야 하는 정보는 다른 정보와 분리해 보관하고, 기간이 끝나면 파기합니다.",
            "이용자가 녹음을 삭제하면 데이터베이스의 녹음, 회의록, 공유 링크와 저장소의 오디오 원본, mp3 변환본을 함께 삭제합니다.",
            "회원 탈퇴 시에는 진행 중인 구독을 해지한 뒤 저장소의 오디오 원본과 mp3 변환본을 지우고, 데이터베이스의 계정과 콘텐츠를 한 번에 삭제합니다.",
            "전자 파일은 복구할 수 없는 방법으로 삭제하며, 서비스는 삭제 표시만 남기는 방식을 쓰지 않습니다. 종이 문서로 된 개인정보는 보유하지 않습니다.",
          ],
        },
        {
          id: "rights",
          title: "정보주체의 권리와 행사 방법",
          body: [
            "이용자는 언제든 자신의 개인정보 열람, 정정, 삭제, 처리 정지와 동의 철회를 요구할 수 있습니다.",
            "서비스 안에서 바로 할 수 있는 것: 녹음 목록에서 개별 녹음 삭제, 설정의 위험 구역에서 전체 초기화와 회원 탈퇴, 설정의 분석 쿠키 항목에서 동의 변경, 실시간 코치 설정에서 등록한 목소리 삭제. 목소리 삭제는 구독이 끝난 뒤에도 할 수 있습니다.",
            "그 밖의 요청은 가입한 이메일로 limelink2024@gmail.com에 보내 주세요. 영업일 기준 3일 안에 답하고, 삭제 요청은 받은 날부터 30일 안에 처리합니다. 법정대리인이나 위임받은 사람도 대신 요청할 수 있습니다.",
            "개인정보 침해로 상담이나 구제가 필요하면 개인정보침해신고센터(국번 없이 118), 개인정보분쟁조정위원회(1833-6972), 대검찰청(1301), 경찰청(182)에 문의할 수 있습니다.",
          ],
        },
        {
          id: "security",
          title: "개인정보의 안전성 확보 조치",
          body: [
            "모든 전송 구간을 HTTPS(TLS)로 암호화합니다.",
            "접근 토큰은 15분 뒤 만료되고, 로그인 갱신 토큰은 해시로만 저장하며 갱신할 때마다 교체합니다. 갱신 토큰은 자바스크립트가 읽을 수 없는 쿠키에 담고, 로그아웃하면 즉시 무효화합니다.",
            "메신저 연동 주소는 AES-256-GCM으로 암호화해 저장하고, 서버 로그에서는 인증 헤더와 쿠키를 가립니다.",
            "요청 횟수 제한, 결제 웹훅 서명 검증, 운영 시스템 접근 권한 제한을 적용하고, 서버 프로그램은 관리자 권한 없이 실행합니다.",
            "결제 수단 정보는 PCI DSS를 준수하는 Paddle이 처리합니다. 개인정보 유출을 알게 되면 법령에 따라 이용자와 관계 기관에 알립니다.",
          ],
        },
        {
          id: "officer",
          title: "개인정보 보호책임자",
          body: [
            "개인정보 보호책임자: 이광복",
            "이메일: limelink2024@gmail.com",
            "개인정보 관련 문의, 불만 처리, 피해 구제는 위 연락처로 보내 주세요.",
          ],
        },
        {
          id: "changes",
          title: "개인정보처리방침의 변경",
          body: [
            "이 방침은 2026년 11월 1일부터 시행합니다.",
            "방침을 바꾸면 시행 7일 전에 이 페이지에 공지하고, 이용자에게 불리하거나 중요한 변경은 30일 전에 공지하며 이메일이나 서비스 안 알림으로도 알립니다.",
          ],
        },
      ],
    },
    terms: {
      effective_at: "2026-11-01",
      previous: [
        { effective_at: "2026-09-04", note: "limenote.org/terms와 limenote.org/refund에 게시한 영문 이용약관과 환불 정책" },
      ],
      sections: [
        {
          id: "purpose",
          title: "목적",
          body: [
            "이 약관은 Hellovelop Inc.가 운영하는 LimeNote(limenote.org, 이하 서비스)의 이용 조건과 절차, 이용자와 운영자의 권리와 의무를 정합니다. 계정을 만들거나 서비스를 이용하면 이 약관에 동의한 것으로 봅니다.",
          ],
        },
        {
          id: "definitions",
          title: "정의",
          body: [
            "서비스: 회의 오디오를 받아써 회의록, 요약 등 문서로 만들고 편집, 공유, 내려받기를 제공하는 웹 서비스입니다.",
            "이용자: 이 약관에 따라 서비스를 이용하는 사람입니다.",
            "콘텐츠: 이용자가 녹음하거나 올린 오디오와 파일, 그로부터 만들어진 전사문, 회의록, 그리고 이용자가 추가한 제목, 태그, 용어집입니다.",
            "크레딧: 서비스 이용량을 재는 단위입니다. 60분 이하 녹음 1건은 1크레딧이며, 60분을 넘으면 시작된 15분마다 0.25크레딧이 더해집니다.",
            "구독: 매달 정해진 크레딧을 받는 월 단위 유료 플랜입니다. 충전팩: 한 번 구매로 크레딧 10개를 받는 상품입니다.",
          ],
        },
        {
          id: "effect",
          title: "약관의 효력과 변경",
          body: [
            "이 약관은 서비스 화면이나 이 페이지에 게시해 효력이 생깁니다.",
            "운영자는 관련 법령을 어기지 않는 범위에서 약관을 바꿀 수 있습니다. 바꿀 때는 시행일과 변경 사유를 시행 7일 전에 이 페이지에 공지하고, 이용자에게 불리하거나 중요한 변경은 30일 전에 공지하며 이메일이나 서비스 안 알림으로도 알립니다.",
            "변경된 약관이 시행된 뒤에도 서비스를 계속 이용하면 변경에 동의한 것으로 봅니다. 동의하지 않으면 이용을 멈추고 탈퇴할 수 있습니다.",
          ],
        },
        {
          id: "service",
          title: "서비스의 제공과 변경",
          body: [
            "이용자는 브라우저에서 회의를 녹음하거나 오디오 파일을 올리고, 서비스는 음성 인식 기술로 받아쓴 뒤 AI 언어 모델로 회의록과 요약을 만듭니다. 이용자는 결과를 검토, 편집, 공유하고 내려받을 수 있습니다.",
            "AI가 만든 전사문과 회의록에는 오류, 누락, 화자 혼동이 있을 수 있습니다. 결과는 초안으로 제공되며, 이용자는 의존하거나 공유하기 전에 내용을 검토해야 합니다.",
            "운영자는 기능을 추가, 변경, 중단할 수 있습니다. 서비스 전체를 종료할 때는 합리적인 기간 전에 알리고, 종료일 이후 기간에 대해 미리 낸 요금을 환불합니다.",
          ],
        },
        {
          id: "account",
          title: "계정",
          body: [
            "계정은 Google 계정으로 로그인해 만듭니다. 이용자는 Google 계정 정보를 안전하게 관리해야 하며, 자신의 계정에서 일어난 활동에 책임을 집니다.",
            "유료 플랜을 구매하려면 만 18세 이상이거나 거주 국가의 성년이어야 합니다.",
            "운영자는 승인된 이메일로만 가입을 허용하는 기간 등 필요한 경우 가입을 거절하거나 제한할 수 있습니다.",
            "이용자는 정확한 정보를 제공하고, 계정이 도용됐다고 의심되면 바로 알려야 합니다.",
            "이용자는 언제든 설정의 위험 구역에서 회원 탈퇴할 수 있습니다. 탈퇴하면 이용 중인 구독은 즉시 종료되고 남은 크레딧은 환불 없이 소멸합니다.",
          ],
        },
        {
          id: "paid",
          title: "유료 서비스와 환불",
          body: [
            "무료 이용자는 가입일을 기준으로 매달 3크레딧을 받습니다. 유료 상품은 미국 달러로 판매하며 Starter 월 $9(18크레딧), Pro 월 $19(53크레딧), Business 월 $99(303크레딧), 충전팩 $15(10크레딧)입니다. 플랜 사이의 차이는 크레딧 양뿐입니다.",
            "표시된 가격에는 부가가치세 등 세금이 포함되지 않으며, 세금은 결제 단계에서 계산해 부과합니다. 가격을 바꾸면 미리 알리고 다음 결제 주기부터 적용합니다.",
            "구독은 해지할 때까지 매달 자동으로 갱신되어 결제됩니다. 구독 크레딧은 결제 주기마다 새로 지급되며 남은 양은 다음 주기로 넘어가지 않습니다. 충전팩 크레딧은 계정이 있는 동안 소멸하지 않으며, 구독 크레딧을 먼저 사용합니다.",
            "구독은 설정에서 언제든 해지할 수 있습니다. 해지해도 현재 결제 주기가 끝날 때까지 플랜이 유지되고, 그 뒤 갱신되지 않으며 남은 구독 크레딧은 소멸합니다. 하위 플랜으로의 변경은 다음 결제 주기부터 적용됩니다.",
            "무료 크레딧과 프로모션 크레딧은 현금 가치가 없으며 환불되지 않습니다.",
            "결제는 판매 대행자(Merchant of Record)인 Paddle.com이 처리하며, Paddle이 영수증 발행과 환불 지급을 맡습니다. 구매 시 Paddle의 Checkout Buyer Terms도 함께 적용됩니다.",
            "환불 기준: 첫 구독 결제 후 14일 안에 요청하고 그 기간에 1크레딧 이하를 썼다면 전액 환불합니다. 갱신 결제는 아래 예외를 빼고 환불하지 않습니다. 충전팩은 구매 후 14일 안에 요청하고 크레딧을 하나도 쓰지 않았다면 전액 환불하며, 하나라도 쓰면 환불하지 않습니다. 남은 기간에 대한 일할 환불은 하지 않습니다.",
            "예외: 운영자 측 장애나 결함으로 결제 주기의 상당 기간 서비스를 쓰지 못한 경우, 같은 구매로 두 번 이상 결제됐거나 해지 후 결제된 경우, 거주 국가의 소비자 보호 법령이 환불을 요구하는 경우에는 위 기준과 관계없이 환불합니다. 구매와 환불을 반복하는 등 남용이 확인되면 환불을 거절할 수 있습니다.",
            "환불 요청 방법: 계정 이메일과 Paddle 주문 번호 또는 청구서 번호를 적어 limelink2024@gmail.com으로 보내거나, Paddle 영수증 메일의 링크를 이용합니다. 승인된 환불은 Paddle이 원래 결제 수단으로 돌려주며 보통 5~10영업일이 걸립니다. 환불된 구매로 받은 구독이나 크레딧은 취소됩니다.",
            "이 조항은 관련 법령에 따른 이용자의 청약철회 등 법정 권리를 제한하지 않습니다.",
          ],
        },
        {
          id: "obligations",
          title: "이용자의 의무와 금지 행위",
          body: [
            "이용자는 자신의 콘텐츠와 서비스 이용 방식에 책임을 지며, 다음 행위를 해서는 안 됩니다.",
            "자신과 다른 참석자에게 적용되는 법령이 요구하는 동의 없이 대화를 녹음, 업로드, 전사하는 행위. 녹음 관련 법은 국가와 지역마다 다르고 모든 참석자의 동의를 요구하는 곳도 있으므로, 녹음 전에 필요한 동의를 받아야 합니다.",
            "불법이거나 타인의 권리를 침해하거나 이용 권한이 없는 콘텐츠를 올리는 행위.",
            "서비스를 이용해 타인을 괴롭히거나 명예를 훼손하거나 감시하거나, 개인정보 보호 법령을 어겨 타인의 정보를 처리하는 행위.",
            "서비스에 무단으로 접근하거나 운영을 방해하거나 이용 한도를 우회하거나 역설계하는 행위.",
            "서비스를 제3자에게 재판매하거나 자동화 수단으로 데이터를 추출하는 행위, 그리고 Paddle의 이용 정책이 금지하는 행위.",
          ],
        },
        {
          id: "content",
          title: "콘텐츠의 권리",
          body: [
            "콘텐츠의 권리는 이용자에게 있습니다.",
            "이용자는 서비스를 운영, 제공, 보호, 개선하는 목적에 한해 운영자가 콘텐츠를 저장, 처리, 전사, 분석, 전송, 표시할 수 있는 비독점적 이용 권한을 줍니다. 여기에는 개인정보처리방침에 적힌 처리 위탁 업체로의 전송이 포함됩니다. 이 권한은 이용자가 콘텐츠나 계정을 삭제하면 끝나며, 법령상 보관해야 하는 사본은 예외입니다.",
            "운영자는 콘텐츠를 판매하거나 광고에 쓰지 않습니다.",
            "이용자는 언제든 개별 녹음을 삭제할 수 있고, 탈퇴하면 녹음, 전사문, 회의록이 삭제됩니다.",
            "공유 링크를 가진 사람은 누구나 해당 회의록을 볼 수 있으며, 링크를 누구에게 줄지는 이용자가 책임집니다.",
          ],
        },
        {
          id: "suspension",
          title: "서비스 중단과 이용 제한",
          body: [
            "운영자는 설비 점검, 장애, 외부 업체 문제 등 부득이한 사유가 있으면 서비스를 일시 중단할 수 있으며, 가능하면 미리 알립니다.",
            "이용자가 이 약관을 중대하게 어기거나, 법령이 요구하거나, 서비스와 다른 이용자를 보호해야 할 때 운영자는 콘텐츠를 삭제하거나 이용을 정지 또는 해지할 수 있습니다.",
          ],
        },
        {
          id: "liability",
          title: "책임의 제한",
          body: [
            "서비스는 현재 상태 그대로 제공되며, 운영자는 법령이 허용하는 범위에서 상품성, 특정 목적 적합성, 전사문과 회의록의 정확성과 완전성을 보증하지 않습니다.",
            "운영자는 법령이 허용하는 범위에서 간접 손해, 특별 손해, 결과적 손해와 이익, 데이터, 신용의 손실에 책임지지 않습니다. 서비스와 관련된 운영자의 총 책임은 사고 발생 전 12개월 동안 이용자가 낸 금액을 넘지 않습니다.",
            "운영자의 고의 또는 중대한 과실로 인한 손해, 그리고 법령상 제한할 수 없는 책임과 소비자로서의 법정 권리에는 이 조항이 적용되지 않습니다.",
          ],
        },
        {
          id: "law",
          title: "분쟁 해결과 준거법",
          body: [
            "운영자는 이용자의 의견과 불만을 성실히 처리하며, 분쟁이 생기면 먼저 협의로 해결하도록 노력합니다.",
            "이 약관은 대한민국 법에 따라 해석됩니다. 서비스와 관련한 소송의 관할 법원은 「민사소송법」에 따른 법원으로 합니다.",
            "이용자가 소비자인 경우 거주 국가의 강행 소비자 보호 법령에 따른 보호를 받을 수 있습니다.",
          ],
        },
      ],
    },
  },
  faq: [
    {
      q: "녹음 파일은 어디에 저장되나요?",
      a: "Cloudflare R2에 계정별 경로로 저장됩니다. 브라우저가 서버를 거치지 않고 R2에 직접 올리며, 전사문과 회의록은 데이터베이스에 저장됩니다.",
    },
    {
      q: "얼마나 긴 녹음까지 되나요? 크레딧은 어떻게 계산하나요?",
      a: "파일 하나는 최대 2GB까지 올릴 수 있습니다. 60분 이하는 1크레딧이고, 60분을 넘으면 시작된 15분마다 0.25크레딧이 더해집니다. 3초 미만 녹음은 과금 전에 거절합니다.",
    },
    {
      q: "변환이 실패하면 크레딧은 어떻게 되나요?",
      a: "같은 작업을 다시 시도할 때는 크레딧을 또 차감하지 않습니다. 메인 AI가 실패하면 다른 회사의 모델이 한 번 더 처리합니다. 녹음이 무음이라 결과가 비어 있어도 사용한 크레딧은 돌려 드리지 않습니다.",
    },
    {
      q: "탈퇴하면 데이터는 바로 지워지나요?",
      a: "설정의 위험 구역에서 회원 탈퇴를 확정하면 구독 해지, 오디오 파일 삭제, 회의록과 계정 삭제가 즉시 진행되며 복구할 수 없습니다. 결제 기록은 전자상거래법에 따라 5년간 보관합니다.",
    },
    {
      q: "사내 용어나 사람 이름이 자꾸 틀리게 받아써져요.",
      a: "용어집에 등록하면 회의록에 자동으로 바로잡아 적용합니다. 원문 전사는 그대로 두고 회의록만 고칩니다. 과거 회의에서 후보를 찾아 미리보기 후 적용할 수도 있습니다.",
    },
  ],
  deletion: {
    in_app_steps: [
      "limenote.org에 로그인한 뒤 설정으로 이동합니다.",
      "위험 구역의 계정 탈퇴 항목에서 회원 탈퇴를 누릅니다.",
      "확인 창에 현재 계정 이메일을 입력하고 영구 삭제를 누릅니다.",
      "처리가 끝나면 로그인 화면에 탈퇴 완료 안내가 나옵니다.",
    ],
    deleted: [
      { item: "이용 중인 Paddle 구독", when: "탈퇴 확정 즉시 해지" },
      { item: "녹음과 업로드한 오디오 원본, mp3 변환본", when: "탈퇴 확정 즉시" },
      { item: "전사문, 회의록, 공유 링크, 변환 작업 기록", when: "탈퇴 확정 즉시" },
      { item: "용어집, 메신저 연동 설정", when: "탈퇴 확정 즉시" },
      { item: "실시간 코치 목소리 정보, 자료, 세션, 발화 기록", when: "탈퇴 확정 즉시" },
      { item: "계정 정보와 로그인 토큰", when: "탈퇴 확정 즉시" },
      { item: "남은 크레딧", when: "환불 없이 즉시 소멸" },
    ],
    retained: [
      {
        item: "구독, 결제, 청약철회 기록과 크레딧 지급, 사용 내역",
        period: "5년",
        basis: "「전자상거래 등에서의 소비자보호에 관한 법률」 제6조 (계약 또는 청약철회 기록, 대금결제와 재화 등의 공급 기록)",
      },
      {
        item: "서버 접속 기록",
        period: "3개월",
        basis: "「통신비밀보호법」 제15조의2와 같은 법 시행령 제41조",
      },
      {
        item: "Paddle이 보관하는 거래 기록",
        period: "Paddle의 방침에 따름",
        basis: "Paddle이 판매 대행자(Merchant of Record)로서 별도로 보관",
      },
    ],
  },
};

export default app;
